'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@eversports/klimt-primitives/Avatar'
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '@eversports/klimt-primitives/Dialog'
import { visuallyHidden } from '@eversports/klimt-utilities/visually-hidden'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import SeoLink from '../../../../components/seoLink'
import { CLOSE_ICON } from '../../../App.icons'

import getUserInitials from './helpers/get-user-initials'
import { User, UserItem } from './UserMenu.types'

interface Props {
  user: User
  menuItems: Array<UserItem>
}

const MobileUserMenu = ({ user, menuItems }: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const t = useTranslations('header')

  const userInitials = getUserInitials(user)

  const toggleDialog = () => {
    setIsOpen(!isOpen)
  }

  return (
    <Dialog open={isOpen} onOpenChange={toggleDialog} aria-labelledby="mobile-user-menu">
      <DialogTrigger asChild className="cursor-pointer md:hidden">
        <div>
          <span id="mobile-user-menu" style={visuallyHidden}>
            {t('mobile-user-menu-accessible-label')}
          </span>
          <Avatar role="dialog">
            <AvatarImage src={user.profilePicture?.xSmall} />
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
        </div>
      </DialogTrigger>
      <DialogContent
        className="flex h-screen max-h-screen w-screen flex-col gap-2 rounded-none"
        onInteractOutside={toggleDialog}
      >
        <DialogClose className="ml-auto mr-0 block">
          <Image priority src={CLOSE_ICON} alt="Close menu" width={20} height={20} />
        </DialogClose>
        <div className="mb-0.5 flex items-center gap-1">
          <Avatar className="size-[50px]">
            <AvatarImage src={user.profilePicture?.xSmall} />
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
          <p className="font-bold">{user.firstName}</p>
        </div>
        <ul>
          {menuItems
            .filter((item) => !item.isHidden)
            .map((item, index) => (
              <li
                key={index}
                className="py-2 first-of-type:border-b first-of-type:border-b-slate-200 last-of-type:border-t last-of-type:border-b-slate-200"
              >
                <SeoLink
                  type="hidden"
                  encodedTo={btoa(item.to)}
                  className="flex items-center gap-1 text-sm"
                  ariaLabel={t(item.translationKey)}
                >
                  <Image src={item.icon} width={16} alt="" /> {/* alt text marked as redundant  */}
                  {t(item.translationKey)}
                </SeoLink>
              </li>
            ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
export default MobileUserMenu
